import React from 'react';
import { Box, Skeleton, TableCell, TableRow } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { sbg } from '../../theme/sbgTokens';
import EmptyState from './EmptyState';
import { useDelayedFlag } from './useDelayedFlag';

export interface TableCardProps {
  /** Toolbar row above the table: SearchField, filters, bulk actions, export. */
  toolbar?: React.ReactNode;
  /** Bulk-selection bar ("12 selected · Approve · Clear selection") — replaces the toolbar while shown. */
  selectionBar?: React.ReactNode;
  /** Footer: TablePagination or a "Load more" button. */
  footer?: React.ReactNode;
  /** Max height before the body scrolls (sticky header stays). */
  maxHeight?: number | string;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
  children: React.ReactNode;
}

/**
 * Table container (book §11.10): surface, 1px border, 12px radius, clips its
 * corners; wide tables scroll inside it, never the page. Put an MUI <Table>
 * (stickyHeader when `maxHeight` is set) inside.
 */
const TableCard: React.FC<TableCardProps> = ({ toolbar, selectionBar, footer, maxHeight, sx, children, ...rest }) => {
  // The scroll region takes a tab stop only when it actually scrolls, so
  // keyboard users can pan wide tables without an extra stop on short ones.
  const regionRef = React.useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = React.useState(true);
  React.useEffect(() => {
    const el = regionRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setScrollable(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, []);
  return (
  <Box
    sx={{
      bgcolor: sbg.bg.surface,
      border: `1px solid ${sbg.border.default}`,
      borderRadius: sbg.radius.lg,
      overflow: 'hidden',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      ...sx,
    }}
  >
    {(selectionBar || toolbar) && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${sbg.border.subtle}`,
          ...(selectionBar ? { bgcolor: sbg.bg.selected } : null),
        }}
      >
        {selectionBar ?? toolbar}
      </Box>
    )}
    <Box
      ref={regionRef}
      role="region"
      aria-label={rest['aria-label']}
      tabIndex={rest['aria-label'] && scrollable ? 0 : undefined}
      sx={{ position: 'relative', overflow: 'auto', maxHeight, minWidth: 0, '&:focus-visible': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: -2 } }}
    >
      {children}
    </Box>
    {footer}
  </Box>
  );
};

export default TableCard;

/**
 * One full-width row for an empty or filtered-out table: the reason and the
 * next step ("No employees match these filters" + Clear filters).
 */
export const TableEmptyRow: React.FC<{
  colSpan: number;
  title: React.ReactNode;
  body?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ colSpan, title, body, action }) => (
  <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
    <TableCell colSpan={colSpan} sx={{ borderBottom: 0 }}>
      <EmptyState compact title={title} body={body} action={action} />
    </TableCell>
  </TableRow>
);

/**
 * Skeleton rows at the real row height, shown after a 300ms delay so fast
 * loads never flash (book §11.10).
 */
export const TableSkeletonRows: React.FC<{ columns: number; rows?: number; delay?: number }> = ({ columns, rows = 6, delay = 300 }) => {
  const visible = useDelayedFlag(true, delay);
  return (
    <>
      {Array.from({ length: rows }).map((_, row) => (
        <TableRow key={row} aria-hidden="true" sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          {Array.from({ length: columns }).map((__, col) => (
            <TableCell key={col}>
              <Skeleton
                variant="rounded"
                height={14}
                width={col === 0 ? '70%' : `${40 + ((row * 7 + col * 13) % 40)}%`}
                sx={{ visibility: visible ? 'visible' : 'hidden' }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};
